import { NavLink, useNavigate } from "react-router-dom";
import { useAdminT } from "../../../hooks/useAdminT";
import { useAdminAuth } from "../../../contexts/AdminAuthContext";

interface AdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function AdminSidebar({ collapsed, onToggle }: AdminSidebarProps) {
  const navigate = useNavigate();
  const t = useAdminT();
  const { currentAdmin, logout } = useAdminAuth();

  const NAV_ITEMS = [
    { path: "/admin", label: t("tabifotoAdmin.nav.dashboard"), icon: "ri-dashboard-line", exact: true },
    { path: "/admin/services", label: t("tabifotoAdmin.nav.services"), icon: "ri-camera-line", exact: false },
    { path: "/admin/portfolio", label: t("tabifotoAdmin.nav.portfolio"), icon: "ri-gallery-line", exact: false },
    { path: "/admin/homepage", label: t("tabifotoAdmin.nav.homepage_display"), icon: "ri-layout-line", exact: false },
    { path: "/admin/content", label: t("tabifotoAdmin.nav.content"), icon: "ri-file-text-line", exact: false },
  ];

  const OPS_ITEMS = [
    { path: "/admin/orders", label: t("tabifotoAdmin.nav.orders"), icon: "ri-file-list-3-line", exact: false },
    { path: "/admin/photographers", label: t("tabifotoAdmin.nav.photographers"), icon: "ri-user-star-line", exact: false },
    { path: "/admin/photographer-applications", label: t("tabifotoAdmin.nav.photographer_applications"), icon: "ri-user-add-line", exact: false },
    { path: "/admin/photographer-availability", label: t("tabifotoAdmin.nav.photographer_availability"), icon: "ri-calendar-check-line", exact: false },
    { path: "/admin/booking-confirmations", label: t("tabifotoAdmin.nav.booking_confirmations"), icon: "ri-checkbox-circle-line", exact: false },
    { path: "/admin/legal-documents", label: t("tabifotoAdmin.nav.legal_documents"), icon: "ri-file-shield-2-line", exact: false },
  ];

  const DETAIL_ITEMS = [
    { path: "/admin/booking-cost-items", label: t("tabifotoAdmin.nav.booking_cost_items"), icon: "ri-money-dollar-circle-line", exact: false },
    { path: "/admin/booking-notes", label: t("tabifotoAdmin.nav.booking_notes"), icon: "ri-sticky-note-line", exact: false },
    { path: "/admin/order-cancellation-logs", label: t("tabifotoAdmin.nav.order_cancellation_logs"), icon: "ri-close-circle-line", exact: false },
    { path: "/admin/order-reschedule-logs", label: t("tabifotoAdmin.nav.order_reschedule_logs"), icon: "ri-calendar-2-line", exact: false },
  ];

  const DISPATCH_ITEMS = [
    { path: "/admin/dispatch", label: "Dispatch & P-System", icon: "ri-send-plane-line", exact: false },
  ];

  const FULFILLMENT_ITEMS = [
    { path: "/admin/assignments", label: "指派管理", icon: "ri-user-follow-line", exact: false },
    { path: "/admin/itineraries", label: "行程管理", icon: "ri-map-pin-time-line", exact: false },
    { path: "/admin/relay", label: "訊息中心", icon: "ri-message-2-line", exact: false },
  ];

  const linkClass = (isActive: boolean) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer whitespace-nowrap ${
      isActive
        ? "bg-[#C8B8A6]/20 text-[#C8B8A6]"
        : "text-white/50 hover:text-white/80 hover:bg-white/5"
    }`;

  const displayName = currentAdmin?.name || currentAdmin?.email || "Admin";
  const displayEmail = currentAdmin?.email;

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <aside className={`flex flex-col h-screen bg-[#2C2C2C] text-white transition-all duration-300 shrink-0 ${collapsed ? "w-16" : "w-60"}`}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
        <button
          onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C8B8A6]/20 text-[#C8B8A6] cursor-pointer shrink-0"
        >
          <i className="ri-camera-2-line text-sm" />
        </button>
        {!collapsed && (
          <span className="text-sm font-light tracking-widest uppercase text-white/80">TaBiFoTo</span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {!collapsed && (
          <p className="text-[10px] uppercase tracking-widest text-white/30 px-4 mb-2">
            {t("tabifotoAdmin.nav.content_section")}
          </p>
        )}
        {NAV_ITEMS.map((item) => (
          <NavLink key={item.path} to={item.path} end={item.exact}
            className={({ isActive }) => linkClass(isActive)}>
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${item.icon} text-base`} />
            </div>
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {!collapsed && (
          <p className="text-[10px] uppercase tracking-widest text-white/30 px-4 mb-2 mt-5">
            {t("tabifotoAdmin.nav.operations_section")}
          </p>
        )}
        {collapsed && <div className="my-2 mx-3 border-t border-white/10" />}
        {OPS_ITEMS.map((item) => (
          <NavLink key={item.path} to={item.path} end={item.exact}
            className={({ isActive }) => linkClass(isActive)}>
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${item.icon} text-base`} />
            </div>
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {!collapsed && (
          <p className="text-[10px] uppercase tracking-widest text-white/30 px-4 mb-2 mt-5">
            Order Detail
          </p>
        )}
        {collapsed && <div className="my-2 mx-3 border-t border-white/10" />}
        {DETAIL_ITEMS.map((item) => (
          <NavLink key={item.path} to={item.path} end={item.exact}
            className={({ isActive }) => linkClass(isActive)}>
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${item.icon} text-base`} />
            </div>
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {!collapsed && (
          <p className="text-[10px] uppercase tracking-widest text-white/30 px-4 mb-2 mt-5">
            Fulfillment
          </p>
        )}
        {collapsed && <div className="my-2 mx-3 border-t border-white/10" />}
        {FULFILLMENT_ITEMS.map((item) => (
          <NavLink key={item.path} to={item.path} end={item.exact}
            className={({ isActive }) => linkClass(isActive)}>
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${item.icon} text-base`} />
            </div>
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {!collapsed && (
          <p className="text-[10px] uppercase tracking-widest text-white/30 px-4 mb-2 mt-5">
            Dispatch
          </p>
        )}
        {collapsed && <div className="my-2 mx-3 border-t border-white/10" />}
        {DISPATCH_ITEMS.map((item) => (
          <NavLink key={item.path} to={item.path} end={item.exact}
            className={({ isActive }) => linkClass(isActive)}>
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${item.icon} text-base`} />
            </div>
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User + logout */}
      <div className="border-t border-white/10">
        {!collapsed ? (
          <div className="px-4 py-3">
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 text-white/60 shrink-0">
                <i className="ri-user-3-line text-xs" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-white/80 font-light truncate">{displayName}</p>
                {displayEmail && (
                  <p className="text-[10px] text-white/30 font-light truncate">{displayEmail}</p>
                )}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-logout-box-r-line" />
              </div>
              {t("tabifotoAdmin.auth.logout")}
            </button>
          </div>
        ) : (
          <button
            onClick={handleLogout}
            title={t("tabifotoAdmin.auth.logout")}
            className="w-full flex items-center justify-center p-3 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-r-line text-base" />
            </div>
          </button>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="flex items-center justify-center p-4 border-t border-white/10 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={collapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} />
        </div>
      </button>
    </aside>
  );
}
